import React, { createContext, useContext, useState } from 'react';

import { IMessage } from '@/shared/api/types/chat-message';

export type ProtocolUpdate = IMessage;

interface ChatChangesContextProps {
  protocolUpdates: ProtocolUpdate[];
  setProtocolUpdates: React.Dispatch<React.SetStateAction<ProtocolUpdate[]>>;
  clearProtocolUpdates: () => void;
  isAnimating: boolean;
  setIsAnimating: React.Dispatch<React.SetStateAction<boolean>>;
  changeAction: 'apply' | 'decline' | null;
  setChangeAction: React.Dispatch<React.SetStateAction<'apply' | 'decline' | null>>;
  applyChanges: () => void;
  declineChanges: () => void;
  hasResolvedBatch: boolean;
  setHasResolvedBatch: React.Dispatch<React.SetStateAction<boolean>>;
}

const ChatChangesContext = createContext<ChatChangesContextProps | null>(null);

export const useChatChanges = () => {
  const ctx = useContext(ChatChangesContext);
  if (!ctx) {
    throw new Error('useChatChanges must be used inside ChatChangesProvider');
  }
  return ctx;
};

export const ChatChangesProvider = ({ children }: { children: React.ReactNode }) => {
  const [protocolUpdates, setProtocolUpdates] = useState<ProtocolUpdate[]>([]);
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  const [changeAction, setChangeAction] = useState<'apply' | 'decline' | null>(null);
  const [hasResolvedBatch, setHasResolvedBatch] = useState<boolean>(false);

  const clearProtocolUpdates = () => setProtocolUpdates([]);

  const applyChanges = () => setChangeAction('apply');
  const declineChanges = () => setChangeAction('decline');

  return (
    <ChatChangesContext.Provider
      value={{
        protocolUpdates,
        setProtocolUpdates,
        clearProtocolUpdates,
        isAnimating,
        setIsAnimating,
        changeAction,
        setChangeAction,
        applyChanges,
        declineChanges,
        hasResolvedBatch,
        setHasResolvedBatch,
      }}
    >
      {children}
    </ChatChangesContext.Provider>
  );
};
